import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Switch,
    Grid,
    Alert,
    Snackbar,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
} from '@mui/material';
import TablePagination from '../../components/common/TablePagination';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import {
    getAllProjects,
    createProject,
    updateProject,
    deleteProject,
} from '../../services/projectService';
import { getAllCustomers } from '../../services/customerService';

const Projects = () => {
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    // Initialize with active projects first
    const [sortConfig, setSortConfig] = useState({
        direction: 'desc', // desc to match the behavior when clicking Active column
        field: 'active'
    });
    const [customers, setCustomers] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        projectNumber: '',
        customerId: '',
        quickbooksName: '',
    });
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalCount, setTotalCount] = useState(0);

    const fetchProjects = useCallback(async (newSortConfig = sortConfig) => {
        try {
            setLoading(true);
            const data = await getAllProjects(page, rowsPerPage, newSortConfig.field, newSortConfig.direction);
            setProjects(data.projects);
            setTotalCount(data.pagination.total);
            setError(null);
        } catch (error) {
            console.error('Error fetching projects:', error);
            setError(error.message);
            if (error.message.includes('authentication') || error.message.includes('token')) {
                window.location.href = '/login';
            }
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, sortConfig]);

    useEffect(() => {
        const init = async () => {
            await Promise.all([
                fetchProjects(),
                fetchCustomers()
            ]);
        };
        init();
    }, [page, rowsPerPage, fetchProjects]); // Re-fetch when pagination changes

    useEffect(() => {
        fetchProjects(sortConfig);
    }, [sortConfig, fetchProjects]); // Re-fetch when sort config changes

    const fetchCustomers = async () => {
        try {
            const data = await getAllCustomers();
            const sortedCustomers = data.sort((a, b) => a.name.localeCompare(b.name));
            setCustomers(sortedCustomers);
        } catch (error) {
            console.error('Error fetching customers:', error);
            setError(error.message);
        }
    };

    const handleFormOpen = (project = null) => {
        if (project) {
            setFormData({
                name: project.name,
                projectNumber: project.projectNumber || '',
                customerId: project.customer?._id || '',
                quickbooksName: project.quickbooksName || '',
            });
            setSelectedProject(project);
        } else {
            setFormData({
                name: '',
                projectNumber: '',
                customerId: '',
                quickbooksName: '',
            });
            setSelectedProject(null);
        }
        setIsFormOpen(true);
    };

    const handleFormClose = () => {
        setIsFormOpen(false);
        setSelectedProject(null);
        setFormData({
            name: '',
            projectNumber: '',
            customerId: '',
            quickbooksName: '',
        });
        setError(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleToggleChange = async (projectId, field) => {
        try {
            const project = projects.find(p => p._id === projectId);
            if (!project) return;

            const updatedValue = !project[field];
            await updateProject(projectId, { [field]: updatedValue });
            fetchProjects();
        } catch (error) {
            console.error(`Error updating ${field}:`, error);
            setError(error.message);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (selectedProject) {
                await updateProject(selectedProject._id, formData);
            } else {
                await createProject(formData);
            }
            handleFormClose();
            fetchProjects();
            setError(null);
        } catch (error) {
            console.error('Error saving project:', error);
            setError(error.message);
            if (error.message.includes('authentication') || error.message.includes('token')) {
                window.location.href = '/login';
            }
        }
    };

    const handleDeleteClick = (project) => {
        setSelectedProject(project);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteProject(selectedProject._id);
            setIsDeleteDialogOpen(false);
            setSelectedProject(null);
            fetchProjects();
            setError(null);
        } catch (error) {
            console.error('Error deleting project:', error);
            setError(error.message);
            if (error.message.includes('authentication') || error.message.includes('token')) {
                window.location.href = '/login';
            }
        }
    };

    const handleErrorClose = () => {
        setError(null);
    };

    const handleSort = (field) => {
        setSortConfig(prev => ({
            direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
            field
        }));
    };

    return (
        <Box sx={{ 
            p: 3,
            '@media (max-width:600px)': {
                p: 1, // Much more reduced padding for mobile
                paddingTop: 0, // No top padding - layout provides spacing
                paddingBottom: '88px', // Account for bottom navigation
            },
            '@media (max-width:900px) and (orientation: landscape)': {
                p: 0.5, // Minimal padding in landscape mode
                paddingTop: 0, // No top padding
                paddingBottom: 0.5, // Minimal bottom padding
                height: 'auto', // Allow natural height
                overflow: 'visible', // Allow content to overflow
                minHeight: 'auto', // Remove height constraints
            }
        }}>
            <Snackbar 
                open={!!error} 
                autoHideDuration={6000} 
                onClose={handleErrorClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleErrorClose} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>

            <Box sx={{ 
                display: 'flex', 
                justifyContent: { xs: 'center', sm: 'flex-end' }, 
                mb: 3 
            }}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleFormOpen()}
                    sx={{
                        '@media (max-width:600px)': {
                            fontSize: '0.875rem',
                            padding: '8px 16px',
                        }
                    }}
                >
                    Add Project
                </Button>
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center" my={4}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer 
                    component={Paper}
                    sx={{
                        overflow: 'auto',
                        '& .MuiTable-root': {
                            borderCollapse: 'separate',
                            borderSpacing: 0
                        }
                    }}
                >
                <Table 
                    size="small" 
                    stickyHeader
                    sx={{
                        '@media (min-width:901px)': {
                            '& .MuiTableCell-root': {
                                padding: '16px',
                            }
                        }
                    }}
                >
                    <TableHead sx={{ bgcolor: '#e0e0e0' }}>
                        <TableRow>
                            <TableCell 
                                onClick={() => handleSort('name')}
                                sx={{ 
                                    cursor: 'pointer',
                                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                                    userSelect: 'none',
                                    width: '50%',
                                    '@media (max-width:900px)': {
                                        padding: '8px',
                                        fontSize: '0.75rem',
                                        width: '45%',
                                    }
                                }}
                            >
                                Project Name
                                {sortConfig.field === 'name' && (
                                    <span style={{ marginLeft: '4px' }}>
                                        {sortConfig.direction === 'asc' ? '▲' : '▼'}
                                    </span>
                                )}
                            </TableCell>
                            <TableCell 
                                align="center"
                                sx={{
                                    width: '15%',
                                    '@media (max-width:900px)': {
                                        padding: '6px',
                                        fontSize: '0.75rem',
                                        width: '16%',
                                    }
                                }}
                            >
                                Billable
                            </TableCell>
                            <TableCell 
                                align="center"
                                onClick={() => handleSort('active')}
                                sx={{ 
                                    cursor: 'pointer',
                                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                                    userSelect: 'none',
                                    width: '15%',
                                    '@media (max-width:900px)': {
                                        padding: '6px',
                                        fontSize: '0.75rem',
                                        width: '16%',
                                    }
                                }}
                            >
                                Active
                                {sortConfig.field === 'active' && (
                                    <span style={{ marginLeft: '4px' }}>
                                        {sortConfig.direction === 'asc' ? '▲' : '▼'}
                                    </span>
                                )}
                            </TableCell>
                            <TableCell 
                                align="center"
                                sx={{
                                    width: '20%',
                                    '@media (max-width:900px)': {
                                        padding: '8px',
                                        fontSize: '0.75rem',
                                        width: '23%',
                                    }
                                }}
                            >
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {projects.map((project) => (
                            <TableRow 
                                key={project._id}
                                sx={{ 
                                    backgroundColor: !project.active ? '#e0e0e0' : 'inherit',
                                    '& td': {
                                        borderBottom: '1px solid rgba(200, 200, 200, 1)'
                                    }
                                }}
                            >
                                <TableCell sx={{ 
                                    width: '50%',
                                    '@media (max-width:900px)': {
                                        padding: '8px',
                                        fontSize: '0.75rem',
                                        width: '45%',
                                    }
                                }}>
                                    {project.name}
                                </TableCell>
                                <TableCell 
                                    align="center"
                                    sx={{
                                        width: '15%',
                                        '@media (max-width:900px)': {
                                            padding: '6px',
                                            textAlign: 'center',
                                            width: '16%',
                                        }
                                    }}
                                >
                                    <Switch
                                        checked={project.billable}
                                        onChange={() => handleToggleChange(project._id, 'billable')}
                                        color="primary"
                                        size="small"
                                        sx={{
                                            '@media (max-width:900px)': {
                                                transform: 'scale(0.8)',
                                            }
                                        }}
                                    />
                                </TableCell>
                                <TableCell 
                                    align="center"
                                    sx={{
                                        width: '15%',
                                        '@media (max-width:900px)': {
                                            padding: '6px',
                                            textAlign: 'center',
                                            width: '16%',
                                        }
                                    }}
                                >
                                    <Switch
                                        checked={project.active}
                                        onChange={() => handleToggleChange(project._id, 'active')}
                                        color="primary"
                                        size="small"
                                        sx={{
                                            '@media (max-width:900px)': {
                                                transform: 'scale(0.8)',
                                            }
                                        }}
                                    />
                                </TableCell>
                                <TableCell 
                                    align="center"
                                    sx={{
                                        width: '20%',
                                        '@media (max-width:900px)': {
                                            padding: '8px',
                                            width: '23%',
                                        }
                                    }}
                                >
                                    <IconButton
                                        onClick={() => handleFormOpen(project)}
                                        size="small"
                                        sx={{
                                            '@media (max-width:900px)': {
                                                padding: '4px',
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: '1rem',
                                                }
                                            }
                                        }}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => handleDeleteClick(project)}
                                        size="small"
                                        sx={{
                                            '@media (max-width:900px)': {
                                                padding: '4px',
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: '1rem',
                                                }
                                            }
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <TablePagination
                        page={page}
                        rowsPerPage={rowsPerPage}
                        totalCount={totalCount}
                        onPageChange={(newPage) => {
                            setPage(newPage);
                            fetchProjects();
                        }}
                        onRowsPerPageChange={(newRowsPerPage) => {
                            setRowsPerPage(newRowsPerPage);
                            setPage(1);
                            fetchProjects();
                        }}
                    />
                </Box>
            </TableContainer>
            )}

            {/* Project Form Dialog */}
            <Dialog
                open={isFormOpen}
                onClose={handleFormClose}
                maxWidth="md"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        bgcolor: 'background.paper',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column'
                    },
                    '& .MuiBackdrop-root': {
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    },
                }}
            >
                <DialogTitle sx={{
                    '@media (max-width:600px)': {
                        fontSize: '1.1rem',
                        padding: '16px',
                        paddingBottom: '8px',
                    }
                }}>
                    {selectedProject ? 'Edit Project' : 'Create Project'}
                </DialogTitle>
                <DialogContent sx={{ 
                    flex: 1, 
                    overflow: 'auto',
                    paddingTop: '24px !important',
                    '@media (max-width:600px)': {
                        padding: '16px',
                        paddingTop: '24px !important',
                    }
                }}>
                    <form id="project-form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="name"
                                    label="Project Name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    fullWidth
                                    required
                                    margin="none"
                                    sx={{
                                        '@media (min-width:601px)': {
                                            marginBottom: '16px',
                                        },
                                        '@media (max-width:600px)': {
                                            marginBottom: '16px',
                                            '& .MuiInputBase-root': {
                                                fontSize: '0.875rem',
                                                height: '40px',
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontSize: '0.875rem',
                                                top: '-2px',
                                                '&.MuiInputLabel-shrunk': {
                                                    top: '-6px',
                                                }
                                            },
                                            '& .MuiOutlinedInput-input': {
                                                padding: '8px 14px',
                                            }
                                        }
                                    }}
                                />
                                <TextField
                                    name="quickbooksName"
                                    label="Quickbooks Name"
                                    value={formData.quickbooksName}
                                    onChange={handleInputChange}
                                    fullWidth
                                    margin="none"
                                    sx={{
                                        '@media (min-width:601px)': {
                                            marginBottom: '16px',
                                        },
                                        '@media (max-width:600px)': {
                                            marginBottom: '16px',
                                            '& .MuiInputBase-root': {
                                                fontSize: '0.875rem',
                                                height: '40px',
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontSize: '0.875rem',
                                                top: '-2px',
                                                '&.MuiInputLabel-shrunk': {
                                                    top: '-6px',
                                                }
                                            },
                                            '& .MuiOutlinedInput-input': {
                                                padding: '8px 14px',
                                            }
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="projectNumber"
                                    label="Project Number"
                                    value={formData.projectNumber}
                                    onChange={handleInputChange}
                                    fullWidth
                                    margin="none"
                                    sx={{
                                        '@media (min-width:601px)': {
                                            marginBottom: '16px',
                                        },
                                        '@media (max-width:600px)': {
                                            marginBottom: '16px',
                                            '& .MuiInputBase-root': {
                                                fontSize: '0.875rem',
                                                height: '40px',
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontSize: '0.875rem',
                                                top: '-2px',
                                                '&.MuiInputLabel-shrunk': {
                                                    top: '-6px',
                                                }
                                            },
                                            '& .MuiOutlinedInput-input': {
                                                padding: '8px 14px',
                                            }
                                        }
                                    }}
                                />
                                <FormControl 
                                    fullWidth 
                                    margin="none"
                                    sx={{
                                        '@media (min-width:601px)': {
                                            marginBottom: '16px',
                                        },
                                        '@media (max-width:600px)': {
                                            marginBottom: '16px',
                                            '& .MuiInputBase-root': {
                                                fontSize: '0.875rem',
                                                height: '40px',
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontSize: '0.875rem',
                                                top: '-2px',
                                                '&.MuiInputLabel-shrunk': {
                                                    top: '-6px',
                                                }
                                            }
                                        }
                                    }}
                                >
                                    <InputLabel>Customer</InputLabel>
                                    <Select
                                        name="customerId"
                                        value={formData.customerId}
                                        onChange={handleInputChange}
                                        label="Customer"
                                        required
                                    >
                                        {customers.map((customer) => (
                                            <MenuItem key={customer._id} value={customer._id}>
                                                {customer.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </form>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 0, justifyContent: 'flex-end' }}>
                    <Button onClick={handleFormClose}>Cancel</Button>
                    <Button type="submit" form="project-form" variant="contained" color="primary">
                        {selectedProject ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={isDeleteDialogOpen}
                onClose={() => setIsDeleteDialogOpen(false)}
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete {selectedProject?.name}? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Projects;
